import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { MoneyIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Box } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { NumericInput } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { columns } from './constants';

const Percent = () => <Box sx={{ paddingLeft: 1 }}>%</Box>;
const round = (value, digits = 6) =>
  Number(Math.round((value + Number.EPSILON) * 10 ** digits) / 10 ** digits);

const commitProposedChange = ({
  draft,
  stateKey,
  rowId,
  handleProposedChange,
  onRowCommit,
  rowData,
}) => {
  let numeric = round(parseFloat(draft), 6);
  if (draft === '' || Number.isNaN(numeric)) {
    numeric = 0;
  }

  handleProposedChange(stateKey, numeric, rowId);
  onRowCommit(rowData);
};

export const factorToPercent = (factor) => {
  if (factor == null || Number.isNaN(factor)) return null;
  if (factor === 1) return 0;
  const pct = factor >= 1 ? (factor - 1) * 100 : factor * 100;
  return round(pct, 2);
};

const normaliseFactor = (raw, percentMode) => {
  let f = raw;
  if (f == null || Number.isNaN(f)) return percentMode ? 1 : 0;

  if (!percentMode && f === 1) return 0;

  if (percentMode && f === 0) return 1;

  return f;
};

const ProposedStateInput = memo(
  ({
    stateKey,
    proposedState,
    handleProposedChange,
    rowId,
    rowData,
    onRowCommit,
    adornment,
    defaultValue,
    disabled = false,
  }) => {
    const external =
      proposedState[`${rowId}-${stateKey}`] ?? defaultValue ?? '';
    const [draft, setDraft] = useState(external);

    useEffect(() => {
      setDraft(proposedState[`${rowId}-${stateKey}`] ?? defaultValue ?? '');
    }, [proposedState]);

    const commit = useCallback(() => {
      commitProposedChange({
        draft,
        stateKey,
        rowId,
        handleProposedChange,
        onRowCommit,
        rowData,
      });
    }, [draft, stateKey, rowId, handleProposedChange, onRowCommit, rowData]);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === 'Enter') {
          commit();
          e.currentTarget.blur();
        }
      },
      [commit],
    );

    const handleChange = useCallback((e) => {
      setDraft(e.target.value);
    }, []);

    return (
      <NumericInput
        value={draft}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        startAdornment={adornment}
        step="0.01"
        HelperTextProps={{ sx: { display: 'none' } }}
        sx={{ paddingRight: 1 }}
        disabled={disabled}
      />
    );
  },
);

const useRows = ({
  sortedAndFilteredResults,
  proposedChangeType,
  forecastId,
  seedInitialProposedState = null,
  syncKey = null,
  onRowCommit = () => {},
  disableInputs,
}) => {
  const [proposedState, setProposedState] = useState({});
  const usePercentage = proposedChangeType === '%';
  const seededRef = React.useRef(false);
  const lastForecastIdRef = React.useRef(null);

  React.useEffect(() => {
    if (!syncKey) return;
    seededRef.current = false;
    setProposedState({});
  }, [syncKey]);

  React.useEffect(() => {
    if (seededRef.current || !seedInitialProposedState) return;
    setProposedState(seedInitialProposedState);
    seededRef.current = true;
  }, [seedInitialProposedState]);

  React.useEffect(() => {
    if (!forecastId) return;

    if (lastForecastIdRef.current !== forecastId) {
      lastForecastIdRef.current = forecastId;
      seededRef.current = false;
      setProposedState({});
    }
  }, [forecastId]);

  const handleProposedChange = useCallback((stateKey, value, rowId) => {
    setProposedState((prevState) => {
      const key = `${rowId}-${stateKey}`;
      if (prevState[key] === value) return prevState;
      return { ...prevState, [key]: value };
    });
  }, []);

  const resetProposedState = useCallback((next = {}) => {
    seededRef.current = false;
    setProposedState(next);
  }, []);

  const rows = useMemo(
    () =>
      sortedAndFilteredResults
        .map((row) => {
          const allowanceData = row?.allowances.reduce((acc, allowance) => {
            const rowId = row.id;
            const column = allowance.uaMessage;
            const currentColumnValue = allowance.currentRate;
            const totalColumnIncome = currentColumnValue * row.numUnits;
            const persistedObj =
              row[`new${column}ProposedChangeValue`] ??
              row[`new${column.replace(/ /g, '')}ProposedChangeValue`];
            const persistedFactor = normaliseFactor(
              persistedObj?.value ?? persistedObj,
              usePercentage,
            );

            const persistedPercent = usePercentage
              ? factorToPercent(parseFloat(persistedFactor))
              : persistedFactor;

            const proposedChangeValue =
              proposedState[`${rowId}-new${column}ProposedChange`] ??
              persistedPercent ??
              0;
            let proposedChange = 0;
            if (proposedChangeValue !== null && proposedChangeValue !== '') {
              const numeric = parseFloat(proposedChangeValue);
              if (!Number.isNaN(numeric)) {
                proposedChange = usePercentage
                  ? round(numeric / 100 + 1, 6)
                  : round(numeric, 2);
              }
            }

            const numUnits = row.numUnits;

            const newColumnValue =
              (usePercentage
                ? currentColumnValue * proposedChange
                : currentColumnValue + proposedChange) || null;

            const newPotentialIncome = newColumnValue * numUnits || null;

            return {
              ...acc,
              [`current${column}`]: {
                value: currentColumnValue,
                variant: 'currency',
              },
              [`total${column}Income`]: {
                value: totalColumnIncome,
                variant: 'currency',
              },
              [`new${column}ProposedChange`]: {
                value: (
                  <ProposedStateInput
                    stateKey={`new${column}ProposedChange`}
                    rowId={rowId}
                    rowData={row}
                    proposedState={proposedState}
                    handleProposedChange={handleProposedChange}
                    adornment={usePercentage ? <Percent /> : <MoneyIcon />}
                    defaultValue={proposedChangeValue}
                    onRowCommit={onRowCommit}
                    disabled={
                      column === 'RD Utility Allowance' || disableInputs
                    }
                  />
                ),
              },
              [`new${column}`]: {
                value: newColumnValue,
                variant: 'currency',
              },
              [`new${column}ProposedChangeValue`]: {
                value: proposedChange,
                variant: 'number',
              },
              [`new${column}PotentialIncome`]: {
                value: newPotentialIncome,
                variant: 'currency',
              },
            };
          }, {});

          const columnData = Object.keys(columns).reduce((acc, column) => {
            const rowId = row.id;
            const currentColumnValue = row[`current${column}`];
            const totalColumnIncome = row[`total${column}Income`];
            const numUnits = row.numUnits;
            const persistedObj =
              row[`new${column}ProposedChangeValue`] ??
              row[`new${column.replace(/ /g, '')}ProposedChangeValue`];

            const persistedFactor = normaliseFactor(
              persistedObj?.value ?? persistedObj,
              usePercentage,
            );

            const persistedPercent = usePercentage
              ? factorToPercent(parseFloat(persistedFactor))
              : persistedFactor;

            const proposedChangeValue =
              proposedState[`${rowId}-new${column}ProposedChange`] ??
              persistedPercent ??
              0;
            let proposedChange = 0;
            if (proposedChangeValue !== null && proposedChangeValue !== '') {
              const numeric = parseFloat(proposedChangeValue);
              if (!Number.isNaN(numeric)) {
                proposedChange = usePercentage
                  ? round(numeric / 100 + 1, 6)
                  : round(numeric, 2);
              }
            }

            const newColumnValue =
              (usePercentage
                ? currentColumnValue * proposedChange
                : currentColumnValue + proposedChange) || null;
            const newPotentialIncome = newColumnValue * numUnits || null;

            return {
              ...acc,
              [`current${column}`]: {
                value: currentColumnValue,
                variant: 'currency',
              },
              [`total${column}Income`]: {
                value: totalColumnIncome,
                variant: 'currency',
              },
              ...(column === 'noteRent'
                ? {
                    actualRentSpread: {
                      value: Math.abs(
                        row['currentbasicRent'] - row['currentnoteRent'] || 0,
                      ),
                      variant: 'currency',
                    },
                  }
                : {}),
              [`new${column}ProposedChange`]: {
                value: (
                  <ProposedStateInput
                    stateKey={`new${column}ProposedChange`}
                    rowId={rowId}
                    rowData={row}
                    proposedState={proposedState}
                    handleProposedChange={handleProposedChange}
                    adornment={usePercentage ? <Percent /> : <MoneyIcon />}
                    defaultValue={proposedChangeValue}
                    onRowCommit={onRowCommit}
                    disabled={disableInputs}
                  />
                ),
              },
              [`new${column}ProposedChangeValue`]: {
                value: proposedChange,
                variant: 'number',
              },
              [`new${column}`]: {
                value: newColumnValue,
                variant: 'currency',
              },
              [`new${column}PotentialIncome`]: {
                value: newPotentialIncome,
                variant: 'currency',
              },
            };
          }, {});

          return {
            id: row.id,
            floorPlan: {
              variant: 'link',
              to: row.floorPlanLink,
              value: row.floorPlan,
            },
            bedsBaths: {
              value: row.bedsBaths,
            },
            unitType: {
              value: row?.unitType,
            },
            numUnits: {
              value: row.numUnits,
              variant: 'number',
            },
            ...allowanceData,
            ...columnData,
          };
        })
        .map((row) => {
          const newBasic = row?.newbasicRent?.value;
          const proposedChange = row?.newnoteRentProposedChangeValue?.value;
          const currentNote = row?.currentnoteRent?.value;
          const currentBasic = row?.currentbasicRent?.value;
          const numUnits = row?.numUnits?.value || 0;

          const shouldUpdate =
            typeof newBasic === 'number' && typeof proposedChange === 'number';

          const hasValidProposedChange = usePercentage
            ? proposedChange !== 1
            : proposedChange !== 0;

          let newNoteValue = null;
          let newNoteRentPotentialIncome = null;

          if (typeof newBasic === 'number') {
            if (hasValidProposedChange && shouldUpdate) {
              newNoteValue = usePercentage
                ? newBasic * proposedChange
                : newBasic + proposedChange;
            } else if (
              typeof currentNote === 'number' &&
              typeof currentBasic === 'number'
            ) {
              if (usePercentage && currentBasic !== newBasic) {
                const rentSpreadPct = Number(
                  (currentNote / currentBasic).toFixed(2),
                );
                newNoteValue = newBasic * rentSpreadPct;
              } else {
                const rentSpread = currentNote - currentBasic;
                newNoteValue = newBasic + rentSpread;
              }
            }

            if (typeof newNoteValue === 'number') {
              newNoteRentPotentialIncome = newNoteValue * numUnits;
            }
          }

          return {
            ...row,
            newnoteRent: {
              ...row.newnoteRent,
              value: newNoteValue,
            },
            newnoteRentPotentialIncome: {
              value: newNoteRentPotentialIncome,
              variant: 'currency',
            },
          };
        }),
    [
      sortedAndFilteredResults,
      proposedState,
      usePercentage,
      handleProposedChange,
      columns,
    ],
  );

  return {
    rows,
    proposedState,
    setProposedState,
    resetProposedState,
  };
};

export default useRows;
